import { assoc, chain, contains, evolve, head, map, mergeDeepLeft, omit, pipe, prop } from 'ramda';
import { compact } from 'ramda-adjunct';
import { MediaType } from '../../../constants/MediaType';
import { MainSource } from '../../../constants/Source';
import { SystemFolderId } from '../../../constants/SystemFolderId';
import { WixMediaRoot } from '../../../constants/WixMediaRoot';
import { encodeFolderId } from '../../hoc/extended-sources/withShutterstock.utils';
import { TMMGRAdapterSettings } from '../../hoc/withMMGRSettings/withMMGRSettings.types';
import { isMediaTypeEnabled } from '../../mediaType';
import { getAdapterWithAdapterSettings } from '../getAdapter';
import {
  IMediaManagerSDKRequestOptions,
  MediaManagerSDKAnyItemWithSectionInfo,
  MediaManagerSDKFile,
  MediaManagerSDKFolder,
} from '../index';
import { getCategoryProperty, getIdOfFirstItem, getSubFoldersOfFirstItem } from './utils';

interface IServerFilesResponse {
  data: MediaManagerSDKFile[];
}

interface IServerFoldersResponse {
  data: MediaManagerSDKFolder[];
}

export class SearchSource {
  settings: any;
  items: {
    search(
      folderId: string,
      query: string,
      options: IMediaManagerSDKRequestOptions,
    ): Promise<{ data: MediaManagerSDKFile[] }>;
  };
  folders: {
    search(
      folderId: string,
      query: string,
      options: IMediaManagerSDKRequestOptions,
    ): Promise<{ data: MediaManagerSDKFolder[] }>;
  };

  constructor(settings: TMMGRAdapterSettings) {
    const { siteFilesSettings, wixMediaSettings, isSecureContext, tabsList, unsplashSettings } = settings;
    const { publicMediaRoot, language } = wixMediaSettings;
    const defaultOption = {
      paging: {
        size: 10, // maximum items per row in search results plus one item. We request at least 1 item more then we can show to easily figure out if need to show "Show All" button
      },
    };

    const siteFilesAdapter = getAdapterWithAdapterSettings(MainSource.SiteFiles, siteFilesSettings);
    const trashAdapter = getAdapterWithAdapterSettings(MainSource.Trash, siteFilesSettings);
    const wixMediaAdapter = getAdapterWithAdapterSettings(MainSource.MediaFromWix, wixMediaSettings);
    const shutterstockAdapter = getAdapterWithAdapterSettings(MainSource.Shutterstock, settings);
    const unsplashAdapter = getAdapterWithAdapterSettings(MainSource.Unsplash, unsplashSettings);
    const visitorUploadsAdapter = getAdapterWithAdapterSettings(MainSource.VisitorsUploads, siteFilesSettings);
    const nttAdapter = getAdapterWithAdapterSettings(MainSource.NTT, wixMediaSettings);

    this.items = {
      search: (folderId, query, options) => {
        const { mediaTypes } = options;
        const newOptions = pipe(
          omit('mediaTypes'),
          mergeDeepLeft(defaultOption),
          mergeDeepLeft(wixMediaSettings.restrictContent ? { filters: { restrictContent: true } } : {}),
          mergeDeepLeft(wixMediaSettings.restrictK12 ? { filters: { restrictK12: true } } : {}),
        )(options);

        // Do not search in FFW and ShutterStock when context is secure
        const shouldLoadFreeFromFix =
          contains(MainSource.MediaFromWix, tabsList) &&
          isMediaTypeEnabled(MediaType.Picture, mediaTypes) &&
          !isSecureContext;

        const shouldLoadShutterstockImages =
          contains(MainSource.Shutterstock, tabsList) &&
          isMediaTypeEnabled(MediaType.Picture, mediaTypes) &&
          !isSecureContext;
        const shouldLoadShutterstockVideos =
          contains(MainSource.ShutterstockVideo, tabsList) &&
          isMediaTypeEnabled(MediaType.Video, mediaTypes) &&
          !isSecureContext;
        const shouldLoadShutterstock = shouldLoadShutterstockImages || shouldLoadShutterstockVideos;

        const shouldLoadUnsplash = contains(MainSource.Unsplash, tabsList) && !isSecureContext;

        const shouldLoadVisitorUploads = contains(MainSource.VisitorsUploads, tabsList) && !isSecureContext;

        const shouldLoadNTT = Boolean(window.isNTT);

        let resources = [
          siteFilesAdapter.items.search(folderId, query, newOptions),
          shouldLoadVisitorUploads &&
            visitorUploadsAdapter.items.search(SystemFolderId.VisitorUploads, query, newOptions),
          trashAdapter.items.trash.search(SystemFolderId.Trash, query, newOptions).then(
            evolve({
              data: map(assoc('source', MainSource.Trash)),
            }),
          ),
        ];

        const getCategoriesMultiSearchPayload = (categories) =>
          map(
            (categoryId) => ({
              mediaType: categories[categoryId].mediaType,
              id: categories[categoryId].subFolders[0].subFolders[0].id,
            }),
            Object.keys(categories),
          );

        if (shouldLoadFreeFromFix) {
          resources = [
            ...resources,
            // Querying Free from Wix consists of three stages
            wixMediaAdapter.folders
              .tree(publicMediaRoot, newOptions) // first we get types
              .then(({ data }) => ({
                firstCategoryGuid: pipe(getSubFoldersOfFirstItem, getSubFoldersOfFirstItem, getIdOfFirstItem)(data),
                mediaType: head(data).mediaType,
                categories: data,
              }))
              .then(({ firstCategoryGuid, mediaType, categories }) =>
                wixMediaAdapter.items
                  .multiSearch(firstCategoryGuid, query, {
                    ...newOptions,
                    language,
                    categories: getCategoriesMultiSearchPayload(categories),
                    mediaType,
                  })
                  .then(({ data }) => ({
                    data: map(
                      (item: MediaManagerSDKAnyItemWithSectionInfo) => ({
                        ...item,
                        source: MainSource.MediaFromWix,
                        subCategoryTitle: getCategoryProperty('name', item.categoryId, categories),
                        parentCategoryId: getCategoryProperty('id', item.categoryId, categories),
                      }),
                      data,
                    ),
                  })),
              ),
          ];
        }

        if (shouldLoadNTT) {
          resources = [
            ...resources,
            // Querying NTT, like Free from Wix, consists of three stages
            nttAdapter.folders
              .list(WixMediaRoot.Ntt, newOptions) // first we get types
              .then(({ data: [{ id: firstCategoryGuid }] }) =>
                // then we get categories
                nttAdapter.folders.list(firstCategoryGuid, newOptions),
              )
              .then(({ data: [{ id: firstSubcategoryGuid, mediaType }] }) =>
                // and after that we perform search in that category
                nttAdapter.items
                  .search(firstSubcategoryGuid, query, {
                    ...newOptions,
                    language,
                    mediaType,
                  })
                  .then((response) => ({
                    ...response,
                    data: map(assoc('source', MainSource.NTT))(response.data),
                  })),
              ),
          ];
        }

        const externalResources = {
          [MainSource.Shutterstock]: [],
          [MainSource.Unsplash]: [],
        };

        if (shouldLoadShutterstock) {
          const addShutterStockResource = (mediaType: MediaType) => {
            externalResources[MainSource.Shutterstock].push(
              shutterstockAdapter.items.search(
                encodeFolderId({
                  mediaType,
                }),
                query,
                { ...newOptions, language: language.replace(/fr|hi|no|ja|zh|uk/, 'en') },
              ),
            );
          };

          if (shouldLoadShutterstockImages) {
            addShutterStockResource(MediaType.Picture);
          }
          if (shouldLoadShutterstockVideos) {
            addShutterStockResource(MediaType.Video);
          }
        }

        if (shouldLoadUnsplash) {
          externalResources[MainSource.Unsplash].push(unsplashAdapter.items.search(folderId, query, newOptions));
        }

        resources = [
          ...resources,
          ...externalResources[MainSource.Unsplash],
          ...externalResources[MainSource.Shutterstock],
        ];

        return Promise.all(compact(resources)).then((responses) => ({
          data: chain<IServerFilesResponse, MediaManagerSDKFile>(prop('data'), responses),
        }));
      },
    };

    this.folders = {
      search: (folderId, query, options) => {
        const newOptions = mergeDeepLeft(defaultOption, options);

        return Promise.all([
          siteFilesAdapter.folders.search(folderId, query, newOptions),
          trashAdapter.folders.trash.search(SystemFolderId.Trash, query, newOptions),
        ]).then((responses) => ({
          data: chain<IServerFoldersResponse, MediaManagerSDKFolder>(prop('data'), responses),
        }));
      },
    };
  }
}
